import useSearchParams, { UseSearchParamsOptions } from '../../../../../hooks/use-search-params.hook';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../../core/queries/projects.queries';
import { InfiniteScrollProps } from '../../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { KnownDeliveriesRequiringAttentionState, KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useTimelineUpdatesEventCentre } from '../../../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useCallback, useMemo } from 'react';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../../../../events/timelineUpdates/timelineUpdates.types';
import {
  UseQueryListDeliveriesRequiringAttentionParams,
  useInfiniteQueryDeliveriesRequiringAttentionList
} from '../../deliveries-requiring-attention.queries';
import {
  liveRefreshIntervalForProjectType,
  useThrottledFirstPageRefetch
} from '../../../../../hooks/use-throttled-first-page-refetch.hook';

const initialControlsState: UseQueryListDeliveriesRequiringAttentionParams = {
  deliveriesRequiringAttentionState: [
    KnownDeliveriesRequiringAttentionState.DeliveredWithFailedNotification,
    KnownDeliveriesRequiringAttentionState.OverdueWithFailedNotification,
    KnownDeliveriesRequiringAttentionState.OverdueWithoutFailedNotification
  ].join(','),
  sortBy: 'deliveredTime',
  sortOrder: KnownSortOrder.Asc
};

const searchParamOptions: UseSearchParamsOptions<UseQueryListDeliveriesRequiringAttentionParams> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true,
    parseDates: ['deliveredFrom', 'deliveredTo']
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  }
};

export function useDeliveriesRequiringAttentionList(projectID: string) {
  const project = useQueryGetProject(projectID);
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);
  const deliveries = useInfiniteQueryDeliveriesRequiringAttentionList(projectID, 1, queryData);

  const projectIDsInScope = useMemo(() => (childProjects.data || []).map((p) => p.id), [childProjects.data]);

  // subscribe to timeline update events
  const refetchFirstPage = useThrottledFirstPageRefetch(
    deliveries.refetch,
    liveRefreshIntervalForProjectType(project.data?.projectType)
  );
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    refetchFirstPage();
  }, [refetchFirstPage]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      if (event.data.projectID && projectIDsInScope.includes(event.data.projectID)) {
        return true;
      }
      return false;
    },
    [projectIDsInScope]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [],
      projectIDs: projectIDsInScope
    }),
    [projectIDsInScope]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: deliveries.hasNextPage || false,
    isFetchingNextPage: deliveries.isFetchingNextPage,
    fetchNextPage: deliveries.fetchNextPage,
    onScrollToBottom() {
      deliveries.fetchNextPage();
    }
  };
  return { project, deliveries, infiniteScroll, formData, setFormData } as const;
}
